import React, { useState, useRef } from 'react';
import classes from './Web3Arbitrage.module.css';
import NetworkScanner from '../../components/NetworkScanner/NetworkScanner';
import './Web3Arbitrage.css';

// Supported blockchains configuration
const SUPPORTED_CHAINS = [
  {
    id: 'ethereum',
    name: 'Ethereum',
    icon: '🔵',
    description: 'Smart contract platform with largest DeFi ecosystem',
    supportedPairs: ['ETH/USDT', 'ETH/USDC', 'ETH/DAI']
  },
  {
    id: 'binance',
    name: 'Binance Smart Chain',
    icon: '🟡',
    description: 'High-performance blockchain with low fees',
    supportedPairs: ['BNB/USDT', 'BNB/BUSD', 'BNB/ETH']
  },
  {
    id: 'polygon',
    name: 'Polygon',
    icon: '🟣',
    description: 'Scalable Ethereum-compatible blockchain',
    supportedPairs: ['MATIC/USDT', 'MATIC/USDC', 'MATIC/ETH']
  },
  {
    id: 'arbitrum',
    name: 'Arbitrum',
    icon: '🔷',
    description: 'Layer 2 scaling solution for Ethereum',
    supportedPairs: ['arb:ETH/USDT', 'arb:ETH/USDC', 'arb:ARB/USDT']
  },
  {
    id: 'optimism',
    name: 'Optimism',
    icon: '🔶',
    description: 'Optimistic rollup scaling solution',
    supportedPairs: ['op:ETH/USDT', 'op:ETH/USDC', 'op:OP/USDT']
  }
];

const Web3Arbitrage = () => {
  const [amount, setAmount] = useState('');
  const [duration, setDuration] = useState('30');
  const [selectedChain, setSelectedChain] = useState('ethereum');
  const [selectedPair, setSelectedPair] = useState('ETH/USDT');
  const [activeDelegations, setActiveDelegations] = useState([
    {
      id: 1,
      chain: 'ethereum',
      pair: 'ETH/USDT',
      amount: 2500,
      startDate: '2024-03-15',
      endDate: '2024-04-14',
      profit: 75.50,
      status: 'active'
    },
    {
      id: 2,
      chain: 'binance',
      pair: 'BNB/USDT',
      amount: 1500,
      startDate: '2024-03-10',
      endDate: '2024-04-09',
      profit: 42.75,
      status: 'active'
    }
  ]);

  const [stats, setStats] = useState({
    totalDelegated: 0,
    activePositions: 0,
    totalProfit: 0,
    successRate: 0,
    averageReturn: 0
  });

  const delegationsRef = useRef(null);

  const handleDelegateFunds = async (e) => {
    e.preventDefault();
    try {
      const newDelegation = {
        id: activeDelegations.length + 1,
        chain: selectedChain,
        pair: selectedPair,
        amount: Number(amount),
        startDate: new Date().toISOString().split('T')[0],
        endDate: new Date(Date.now() + Number(duration) * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        profit: 0,
        status: 'active'
      };

      setActiveDelegations([...activeDelegations, newDelegation]);
      setStats(prevStats => ({
        ...prevStats,
        totalDelegated: prevStats.totalDelegated + Number(amount),
        activePositions: prevStats.activePositions + 1,
        successRate: 95.5,
        averageReturn: 2.8
      }));

      setAmount('');
    } catch (error) {
      console.error('Error delegating funds:', error);
    }
  };

  const handleWithdraw = (delegationId) => {
    setActiveDelegations(activeDelegations.map(d => 
      d.id === delegationId ? { ...d, status: 'withdrawn' } : d
    ));
  };

  const handleChainSelect = (chainId) => {
    setSelectedChain(chainId);
    // Select the first pair from the selected chain's supported pairs
    const chain = SUPPORTED_CHAINS.find(c => c.id === chainId);
    if (chain && chain.supportedPairs.length > 0) {
      setSelectedPair(chain.supportedPairs[0]);
    }
  };

  const handlePairSelect = (pair, e) => {
    e.stopPropagation();
    const chain = SUPPORTED_CHAINS.find(c => c.supportedPairs.includes(pair));
    // Find the chain that supports this pair
    if (chain) {
      // First select the chain
      setSelectedChain(chain.id);
      // Then select the pair
      setSelectedPair(pair);
    }
  };

  const scrollToDelegations = () => {
    delegationsRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className={classes.container}>
      <div className={classes.header}>
        <h1>Web3 Arbitrage Trading</h1>
        <p>Delegate your funds for automated cross-chain arbitrage opportunities</p>
      </div>

      <div className={classes.content}>
      

        <div className={classes.infoCard}>
          <h2>Supported Blockchains</h2>
          <div className={classes.chainsList}>
            {SUPPORTED_CHAINS.map(chain => (
              <div 
                key={chain.id} 
                className={`${classes.chainItem} ${selectedChain === chain.id ? classes.selected : ''}`}
                onClick={() => handleChainSelect(chain.id)}
              >
                <div className={classes.chainContent}>
                  <div className={classes.chainInfo}>
                    <span className={classes.chainIcon}>{chain.icon}</span>
                    <span className={classes.chainName}>{chain.name}</span>
                  </div>
                  <div className={classes.supportedPairs}>
                    {chain.supportedPairs.map(pair => {
                      const isSelectedChain = selectedChain === chain.id;
                      const isSelectedPair = selectedPair === pair;
                      return (
                        <span 
                          key={pair}
                          className={`${classes.pairTag} ${isSelectedChain && isSelectedPair ? classes.selected : ''}`}
                          onClick={(e) => handlePairSelect(pair, e)}
                        >
                          {pair}
                          {isSelectedChain && isSelectedPair && <span className={classes.checkmark}>✓</span>}
                        </span>
                      );
                    })}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className={classes.delegationForm}>
          <h2>Delegate Funds</h2>
          <form onSubmit={handleDelegateFunds}>
            <div className={classes.formGroup}>
              <label htmlFor="amount">Amount to Delegate (USD)</label>
              <input
                type="number"
                id="amount"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                min="100"
                step="100"
                required
                placeholder="Enter amount"
              />
            </div>

            <div className={classes.formGroup}>
              <label htmlFor="duration">Delegation Duration (Days)</label>
              <select
                id="duration"
                value={duration}
                onChange={(e) => setDuration(e.target.value)}
                required
              >
                <option value="30">30 Days</option>
                <option value="60">60 Days</option>
                <option value="90">90 Days</option>
                <option value="180">180 Days</option>
              </select>
            </div>

            <div className={classes.selectedOptions}>
              <div className={classes.option}>
                <span className={classes.optionLabel}>Selected Chain:</span>
                <span className={classes.optionValue}>
                  {SUPPORTED_CHAINS.find(c => c.id === selectedChain)?.name}
                </span>
              </div>
              <div className={classes.option}>
                <span className={classes.optionLabel}>Trading Pair:</span>
                <span className={classes.optionValue}>{selectedPair}</span>
              </div>
            </div>

            <button type="submit" className={classes.submitButton}>
              Delegate Funds
            </button>
          </form>
        </div>

        
        <div className={classes.section}>
          <button 
            onClick={scrollToDelegations}
            className={classes.scrollButton}
          >
            View Active Delegations
          </button>
          <h2>Arbitrage Opportunity Scanner Bot</h2>
          <NetworkScanner />
        </div>
        <div ref={delegationsRef} className={classes.delegationsSection}>
          <h2>My Active Delegations</h2>
          <div className={classes.delegationsList}>
            {activeDelegations.filter(d => d.status === 'active').map(delegation => (
              <div key={delegation.id} className={classes.delegationCard}>
                <div className={classes.delegationHeader}>
                  <div className={classes.chainInfo}>
                    <span className={classes.chainIcon}>
                      {SUPPORTED_CHAINS.find(c => c.id === delegation.chain)?.icon}
                    </span>
                    <span className={classes.pair}>{delegation.pair}</span>
                  </div>
                  <span className={`${classes.status} ${classes[delegation.status]}`}>
                    {delegation.status}
                  </span>
                </div>

                <div className={classes.delegationDetails}>
                  <div className={classes.detailItem}>
                    <span className={classes.detailLabel}>Amount</span>
                    <span className={classes.detailValue}>${delegation.amount.toFixed(2)}</span>
                  </div>
                  <div className={classes.detailItem}>
                    <span className={classes.detailLabel}>Start Date</span>
                    <span className={classes.detailValue}>{delegation.startDate}</span>
                  </div>
                  <div className={classes.detailItem}>
                    <span className={classes.detailLabel}>End Date</span>
                    <span className={classes.detailValue}>{delegation.endDate}</span>
                  </div>
                  <div className={classes.detailItem}>
                    <span className={classes.detailLabel}>Profit</span>
                    <span className={classes.detailValue}>${delegation.profit.toFixed(2)}</span>
                  </div>
                </div>

                <div className={classes.delegationActions}>
                  <button 
                    className={classes.withdrawButton}
                    onClick={() => handleWithdraw(delegation.id)}
                  >
                    Withdraw
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className={classes.statsCard}>
          <h2>Trading Statistics</h2>
          <div className={classes.stats}>
            <div className={classes.statItem}>
              <span className={classes.statLabel}>Total Delegated</span>
              <span className={classes.statValue}>${stats.totalDelegated.toFixed(2)}</span>
            </div>
            <div className={classes.statItem}>
              <span className={classes.statLabel}>Active Positions</span>
              <span className={classes.statValue}>{stats.activePositions}</span>
            </div>
            <div className={classes.statItem}>
              <span className={classes.statLabel}>Success Rate</span>
              <span className={classes.statValue}>{stats.successRate}%</span>
            </div>
            <div className={classes.statItem}>
              <span className={classes.statLabel}>Average Return</span>
              <span className={classes.statValue}>{stats.averageReturn}%</span>
            </div>
          </div>
        </div>

        {/* <div className={classes.infoCard}>
          <h2>How It Works</h2>
          <div className={classes.howItWorks}>
            <div className={classes.step}>
              <h3>1. Fund Delegation</h3>
              <p>Delegate your funds to our automated arbitrage system. Choose your preferred blockchain and trading pair.</p>
            </div>
            <div className={classes.step}>
              <h3>2. Price Monitoring</h3>
              <p>Our system continuously monitors price differences across multiple DEXs and chains in real-time.</p>
            </div>
            <div className={classes.step}>
              <h3>3. Automated Execution</h3>
              <p>When profitable opportunities are detected, trades are automatically executed across different platforms.</p>
            </div>
            <div className={classes.step}>
              <h3>4. Profit Collection</h3>
              <p>Profits are automatically collected and added to your delegated amount, compounding your returns.</p>
            </div>
          </div>
        </div> */}
      </div>
    </div>
  );
};

export default Web3Arbitrage; 